/**
 * TeamForm's auto-save configuration, kept out of the component file so it
 * can be tested on its own.
 */

import type { FieldConfig } from '@/hooks/useAutoSave';

/**
 * Parse throughput data string to array of numbers.
 * Returns empty array if no valid numbers found.
 */
export function parseThroughputData(value: unknown): number[] {
  const nums = String(value)
    .split(',')
    .map((s: string) => parseFloat(s.trim()))
    .filter((n: number) => !isNaN(n));
  return nums;
}

/** The team fields auto-save tracks, as named on the form's team. */
export const TEAM_AUTO_SAVE_FIELDS = ['name', 'throughput_data', 'throughput_scale', 'wip', 'color'] as const;

export function isTeamAutoSaveField(field: string): field is keyof TeamAutoSaveValues {
  return (TEAM_AUTO_SAVE_FIELDS as readonly string[]).includes(field);
}

/** The team fields auto-save tracks, keyed as TeamForm watches them. */
export type TeamAutoSaveValues = {
  name: unknown;
  throughput_data: unknown;
  throughput_scale: unknown;
  wip: unknown;
  color: unknown;
};

/**
 * What TeamForm auto-saves. A dataset team's throughput series is the
 * server's alone — it has no input, and a PATCH of it could overwrite a newer
 * cut — so it is never sent for one (shipcast-d8r.40).
 */
export function teamAutoSaveFields(fromDataset: boolean): FieldConfig<TeamAutoSaveValues>[] {
  return [
    { name: 'name' },
    {
      name: 'throughput_data',
      transform: (v) => parseThroughputData(v),
      skipIf: (v) => fromDataset || (v as number[]).length === 0,
    },
    { name: 'throughput_scale', transform: (v) => Number(v) },
    { name: 'wip', transform: (v) => Number(v) },
    { name: 'color' },
  ];
}

/**
 * The auto-saved team field a cache-sync write at `path` set on the team at
 * `teamIndex` (`teams.2.wip`), with its value. TeamForm re-bases it, so it is
 * not saved back. The sync never rewrites the whole `teams` array: the tabs
 * are always on screen, so it adds, removes and reorders teams through their
 * field-array operations (shipcast-nad4), which leave each team's values as
 * they were.
 */
export function syncedTeamFields(
  path: string,
  value: unknown,
  teamIndex: number,
): Array<[keyof TeamAutoSaveValues, unknown]> {
  const own = `teams.${teamIndex}.`;
  if (path.startsWith(own)) {
    const field = path.slice(own.length);
    return isTeamAutoSaveField(field) ? [[field, value]] : [];
  }
  return [];
}
